import type { Deck, DeckPressReleaseLink } from '~~/entities/deck'
import type { DeckAPIQueryParams, DeckAPIBodyParams } from '~~/entities/api'
import { normalizeDeckFetchError } from '~~/lib/http-errors'

const COOKIE_EXPIRATION = 60 * 60 * 24 * 14 // 14 days

export function useDecks() {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))

  const {
    createItem: createDeck,
    listItemsPaginated: listDecks,
    getItem: _getItem,
    updateItem: updateDeck,
    deleteItem: deleteDeck,
  } = useAPIClient<Deck, DeckAPIQueryParams, DeckAPIBodyParams>(`/v2/decks`)

  async function getDeckById(id: string, params: DeckAPIQueryParams): Promise<Deck> {
    const authIdCookie = useCookie(`auth${id}`, { maxAge: COOKIE_EXPIRATION })

    try {
      return await _getItem(id, params, {
        headers: {
          ...globalFetchHeaders.value,
          'x-deck-password': authIdCookie.value ? authIdCookie.value : '',
        },
      })
    } catch (e) {
      throw normalizeDeckFetchError(e, Boolean(useUser().state.value.user))
    }
  }

  async function cloneDeck({
    deckId,
    payload,
    options,
  }: {
    deckId: string | number
    payload: DeckAPIBodyParams
    options: { duplicateModels?: boolean; duplicateDrillData?: boolean }
  }) {
    const params = new URLSearchParams()
    if (options.duplicateModels) params.append('include', '3dmodel')
    if (options.duplicateDrillData) params.append('include', 'drill')
    const queryString = params.toString() ? `?${params.toString()}` : ''

    return $fetch<Deck>(`/v2/decks/${deckId}/clone${queryString}`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: payload,
    })
  }

  async function requestDeckAccess(deckId: string | number, payload: { name: string; email: string; reason: string }): Promise<void> {
    return await $fetch<undefined>(`/v2/decks/${deckId}/request_access`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: payload,
    })
  }

  async function createPressReleaseLink(deckId: string | number): Promise<DeckPressReleaseLink> {
    const body = await $fetch<{ uuid: string; created_at: string }>(`/v2/decks/${deckId}/press_release_link/`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: {},
    })
    return { uuid: body.uuid, created_at: body.created_at }
  }

  async function deletePressReleaseLink(deckId: string | number): Promise<void> {
    await $fetch<undefined>(`/v2/decks/${deckId}/press_release_link/`, {
      ...useFetchSharedOptions(),
      method: 'DELETE',
    })
  }

  return {
    createDeck,
    listDecks,
    getDeckById,
    updateDeck,
    deleteDeck,
    cloneDeck,
    requestDeckAccess,
    createPressReleaseLink,
    deletePressReleaseLink,
  }
}
